import { TokenAmount, TokenLabel } from "@/components/brand/TokenAmount";
import { AnimatedNumber } from "@/components/defi/AnimatedNumber";
import {
	useBadDebtSummary,
	useLiquidityTreasury,
	useSurplusPhase,
	useTreasuryFlows,
	useTreasuryOverview,
} from "@/hooks/useTreasury";
import { docsArticlePath } from "@/lib/docs/parse";
import { Pagination } from "@/components/ui/Pagination";
import { normalizeTokenId } from "@/lib/token-assets";
import { cn, formatNum } from "@/lib/utils";
import { useState } from "react";
import { Link } from "react-router-dom";

function sleeveToken(
	label: string,
	tokenId?: string,
): "ICP" | "MMK" | "ckUSDC" | "ckBTC" | "ckETH" | "USDM" | null {
	// Prefer explicit tokenId from treasury rows (Ops ICP, ckBTC sleeve, …)
	const fromId = tokenId ? normalizeTokenId(tokenId) : null;
	if (fromId) {
		return fromId as
			| "ICP"
			| "MMK"
			| "ckUSDC"
			| "ckBTC"
			| "ckETH"
			| "USDM";
	}
	return normalizeTokenId(label) as
		| "ICP"
		| "MMK"
		| "ckUSDC"
		| "ckBTC"
		| "ckETH"
		| "USDM"
		| null;
}

function surplusHealth(
	surplus: number,
	debt: number,
): { label: string; className: string } {
	const buffer = debt > 0 ? surplus / debt : 1;
	if (buffer >= 0.08) return { label: "Healthy", className: "text-primary" };
	if (buffer >= 0.03) return { label: "Moderate", className: "text-warning" };
	return { label: "Critical", className: "text-danger" };
}

export default function TreasuryPage() {
	const {
		data: overview,
		isLoading,
		isError: overviewError,
		error: overviewErr,
	} = useTreasuryOverview();
	const { data: liq, isError: liqError } = useLiquidityTreasury();
	const { data: badDebt, isError: badDebtError } = useBadDebtSummary();
	const { data: surplusPhase } = useSurplusPhase();
	const [flowOffset, setFlowOffset] = useState(0);
	const flowPageSize = 10;
	const {
		data: flowPage,
		isError: flowsError,
		error: flowsErr,
	} = useTreasuryFlows(flowPageSize, flowOffset);
	const flows = flowPage?.rows ?? [];
	const flowTotal = flowPage?.total ?? 0;
	const health = surplusHealth(
		overview?.surplusUsdm ?? 0,
		overview?.totalDebtUsdm ?? 1,
	);
	const rows = liq?.rows ?? [];
	const sourceLabel =
		overview?.source === "live"
			? "Live"
			: overview?.source === "fixture"
				? "Unavailable"
				: "—";
	const openBadDebt = badDebt?.openBadDebtUsdm ?? 0;
	const badDebtAlert = openBadDebt > 0;

	return (
		<div className="container mx-auto space-y-8 px-4 py-6 sm:py-8">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div id="section-surplus" className="scroll-mt-24">
					<p className="section-eyebrow">Treasury</p>
					<h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
						Protocol treasury
					</h1>
					<p className="mt-1 max-w-xl text-sm text-muted-foreground">
						Surplus, sleeves, and flows. Transparent on-chain holdings.
					</p>
					<p className="mt-1 text-xs">
						<Link
							to={docsArticlePath("treasury", "overview")}
							className="font-semibold text-primary underline"
						>
							Docs · how it works
						</Link>
					</p>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<span className="rounded-full border border-border px-3 py-1 text-[10px] font-semibold uppercase text-muted-foreground">
						{sourceLabel}
						{liq?.source === "live" ? " · sleeves live" : ""}
						{flowPage?.source === "live" ? " · flows live" : ""}
					</span>
					<span
						className={cn(
							"rounded-full border border-border px-3 py-1 text-xs font-semibold",
							health.className,
						)}
					>
						Surplus buffer · {health.label}
					</span>
					{surplusPhase?.source === "live" && (
						<span
							className={cn(
								"rounded-full border px-3 py-1 text-[10px] font-semibold uppercase",
								surplusPhase.phaseB
									? "border-primary/40 bg-primary/10 text-primary"
									: "border-border text-muted-foreground",
							)}
							title={
								surplusPhase.phaseB
									? "Primary only-up mint complete: waterfall MMK leg pays USDM pro-rata to stakers"
									: "Primary only-up incomplete: waterfall MMK leg buys non-voting treasury MMK"
							}
						>
							{surplusPhase.phaseB
								? "Phase B · staker yield"
								: "Phase A · treasury MMK buy"}
						</span>
					)}
				</div>
			</div>

			{(overviewError || liqError || flowsError || badDebtError) && (
				<div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
					Could not load some treasury data from the protocol.
					{overviewErr instanceof Error ? ` ${overviewErr.message}` : ""}
					{flowsErr instanceof Error ? ` ${flowsErr.message}` : ""}
					{" "}Check canister env and replica.
				</div>
			)}

			{isLoading ? (
				<div className="grid gap-3 sm:grid-cols-4">
					{Array.from({ length: 4 }).map((_, i) => (
						<div key={i} className="skeleton-shimmer h-24" />
					))}
				</div>
			) : overviewError && !overview ? (
				<p className="text-sm text-muted-foreground">
					Treasury overview unavailable.
				</p>
			) : (
				<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
					<div className="card-surface p-4">
						<p className="text-[10px] uppercase text-muted-foreground">Surplus</p>
						<div className="mt-2">
							<TokenAmount
								id="USDM"
								value={overview?.surplusUsdm ?? 0}
								decimals={0}
								size="md"
							/>
						</div>
					</div>
					<div className="card-surface p-4">
						<p className="text-[10px] uppercase text-muted-foreground">
							USDM debt
						</p>
						<div className="mt-2">
							<TokenAmount
								id="USDM"
								value={overview?.totalDebtUsdm ?? 0}
								decimals={0}
								size="md"
							/>
						</div>
					</div>
					<div className="card-surface p-4">
						<p className="text-[10px] uppercase text-muted-foreground">Vault TVL</p>
						<p className="mt-2 font-mono-amount text-xl font-bold">
							$
							<AnimatedNumber value={overview?.vaultTvlUsd ?? 0} decimals={0} />
						</p>
					</div>
					<div className="card-surface p-4">
						<p className="text-[10px] uppercase text-muted-foreground">
							Collateralization
						</p>
						<p className="mt-2 font-mono-amount text-xl font-bold">
							<AnimatedNumber
								value={overview?.collateralizationRatio ?? 0}
								decimals={2}
							/>
							×
						</p>
					</div>
				</div>
			)}

			{overview?.byAsset && overview.byAsset.length > 0 ? (
				<section className="card-surface p-5">
					<p className="section-eyebrow mb-3">Locked by asset</p>
					<ul className="space-y-2">
						{overview.byAsset.map((a) => (
							<li
								key={a.asset}
								className="flex items-center justify-between text-sm"
							>
								<TokenLabel id={a.asset} size="sm" />
								<span className="font-mono-amount text-muted-foreground">
									${formatNum(a.lockedUsd, 0)} · debt{" "}
									{formatNum(a.debt, 0)}
								</span>
							</li>
						))}
						<li className="flex items-center justify-between border-t border-border pt-2 text-sm font-bold">
							<span>Total</span>
							<span className="font-mono-amount">
								$
								{formatNum(
									overview.byAsset.reduce((s, a) => s + a.lockedUsd, 0),
									0,
								)}{" "}
								· debt{" "}
								{formatNum(
									overview.byAsset.reduce((s, a) => s + a.debt, 0),
									0,
								)}{" "}
								USDM
							</span>
						</li>
					</ul>
					<p className="mt-2 text-[11px] text-muted-foreground">
						Total debt should match USDM debt above (
						{formatNum(overview.totalDebtUsdm ?? 0, 0)} USDM).
					</p>
				</section>
			) : null}

			<section
				id="section-bad-debt"
				className={cn(
					"card-surface scroll-mt-24 p-5",
					badDebtAlert && "border-danger/40 bg-danger/5",
				)}
			>
				<div className="mb-3 flex flex-wrap items-center justify-between gap-2">
					<div>
						<p className="section-eyebrow">Bad debt</p>
						<p className="text-sm text-muted-foreground">
							Auction shortfalls settled only from surplus USDM (senior to
							waterfall). Monthly timer + admin settle; never via MMK mint.
						</p>
					</div>
					<Link
						to={docsArticlePath("liquidation", "bad-debt")}
						className="text-xs font-semibold text-primary underline"
					>
						Docs
					</Link>
				</div>
				<div className="grid gap-3 sm:grid-cols-3">
					<div className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2">
						<p className="text-[10px] font-semibold uppercase text-muted-foreground">
							Open bad debt
						</p>
						<div className="mt-1">
							<TokenAmount
								id="USDM"
								value={openBadDebt}
								decimals={2}
								size="sm"
							/>
						</div>
					</div>
					<div className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2">
						<p className="text-[10px] font-semibold uppercase text-muted-foreground">
							Settlement reserve
						</p>
						<div className="mt-1">
							<TokenAmount
								id="USDM"
								value={badDebt?.reserveUsdm ?? 0}
								decimals={2}
								size="sm"
							/>
						</div>
					</div>
					<div className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2">
						<p className="text-[10px] font-semibold uppercase text-muted-foreground">
							Events
						</p>
						<p className="mt-1 font-mono-amount text-lg font-semibold">
							{badDebt?.eventCount ?? 0}
						</p>
					</div>
				</div>
				{badDebtAlert ? (
					<p className="mt-3 text-xs font-medium text-danger">
						Open shortfalls reduce protocol health until surplus settles them
						(auto timer keeps 10% surplus buffer).
					</p>
				) : (
					<p className="mt-3 text-xs text-muted-foreground">
						No open bad debt
						{badDebt?.source === "live" ? " (live)" : ""}.
					</p>
				)}
			</section>

			<section className="card-surface p-5">
				<p id="section-sleeves" className="section-eyebrow mb-3 scroll-mt-24">
					Sleeves
				</p>
				<ul className="space-y-2">
					{rows.map((s) => {
						const tok = sleeveToken(s.label, s.tokenId);
						return (
							<li
								key={s.label}
								className="flex items-center justify-between gap-2 rounded-xl border border-border px-3 py-2.5 text-sm"
							>
								<div className="flex min-w-0 flex-col gap-0.5">
									{tok ? (
										<TokenLabel id={tok} size="sm" />
									) : (
										<span className="font-medium">{s.label}</span>
									)}
									{tok ? (
										<span className="truncate text-[10px] text-muted-foreground">
											{s.label}
										</span>
									) : null}
								</div>
								<span className="font-mono-amount shrink-0 text-muted-foreground">
									{s.amount}
								</span>
							</li>
						);
					})}
					{rows.length === 0 ? (
						<li className="text-sm text-muted-foreground">No sleeve data</li>
					) : null}
				</ul>
			</section>

			<section className="card-surface p-5">
				<p className="section-eyebrow mb-3">
				<span id="section-flows" className="scroll-mt-24">
					Recent flows
				</span>
					{flowPage?.source === "live" ? (
						<span className="ml-2 font-normal normal-case text-muted-foreground">
							· on-chain
						</span>
					) : null}
				</p>
				{flowsError ? (
					<p className="text-sm text-muted-foreground">
						Treasury flows could not be loaded.
					</p>
				) : flows.length === 0 ? (
					<p className="text-sm text-muted-foreground">
						No treasury flow events yet (empty ring or fresh deploy).
					</p>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full min-w-[720px] text-left text-sm">
							<thead>
								<tr className="border-b border-border text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
									<th className="px-2 py-2">Kind</th>
									<th className="px-2 py-2">From</th>
									<th className="px-2 py-2">To</th>
									<th className="px-2 py-2 text-right">Amount</th>
								</tr>
							</thead>
							<tbody>
								{flows.map((f) => {
									const short = (s: string) =>
										s.length > 22 ? `${s.slice(0, 10)}…${s.slice(-6)}` : s;
									return (
										<tr
											key={f.id}
											className="border-b border-border/40 last:border-0"
										>
											<td className="px-2 py-2.5 align-top">
												<p className="font-semibold">
													{f.kind}{" "}
													<span className="font-normal text-muted-foreground">
														· {f.direction}
													</span>
												</p>
												<p className="line-clamp-2 text-[11px] text-muted-foreground">
													{f.detail}
												</p>
											</td>
											<td
												className="max-w-[10rem] px-2 py-2.5 align-top font-mono text-[11px] text-muted-foreground"
												title={f.fromAccount}
											>
												{short(f.fromAccount)}
											</td>
											<td
												className="max-w-[10rem] px-2 py-2.5 align-top font-mono text-[11px] text-muted-foreground"
												title={f.toAccount}
											>
												{short(f.toAccount)}
											</td>
											<td className="px-2 py-2.5 text-right align-top">
												{normalizeTokenId(f.token) ? (
													<TokenAmount
														id={normalizeTokenId(f.token)!}
														value={f.amount}
														decimals={
															normalizeTokenId(f.token) === "ckBTC" ? 4 : 0
														}
														size="sm"
													/>
												) : (
													<span className="font-mono-amount">
														{formatNum(f.amount, 2)} {f.token}
													</span>
												)}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
				{flowTotal > 0 ? (
					<div className="mt-4">
						<Pagination
							offset={flowOffset}
							pageSize={flowPageSize}
							total={flowTotal}
							onOffsetChange={setFlowOffset}
						/>
					</div>
				) : null}
			</section>
		</div>
	);
}
